/**
 * 기본 수수료율 설정 — 레거시 `baseFeeRateSetting.js` UI·로직 포팅.
 * 구간(이상/미만) · 고정(사용자 선택 모달) · 전월 불러오기 · dirty 확인 모달.
 */
import {
  useBasicFeePolicy,
  useCopyBasicFeePolicy,
  useRegisterBasicFeePolicy,
} from '@/entities/fee-rate/hooks/useFeeRates';
import { userService } from '@/entities/user/api/user.api';
import {
  buildBasicFeePolicyPayload,
  createFixedRows,
  createRangeRows,
  formatAmountInput,
  getPreviousRefYm,
  handleRateStep,
  normalizeRangeMins,
  parseAmountInput,
  toFixedRows,
  toRangeRows,
  type FixedFeeRowUi,
  type FixedFeeUserUi,
  type RangeFeeRowUi,
} from '@/pages/fee-rates/baseFeeRateSettingUtils';
import {
  formatRateInput,
  formatRefYmDot,
  getCurrentRefYm,
  roundRate,
} from '@/pages/fee-rates/feeRateUtils';
import { useMenuAccess } from '@/layouts/MenuAccessContext';
import { LoadingSpinner } from '@/shared/components/LoadingSpinner';
import { MonthPicker } from '@/shared/components/DatePicker';
import { MenuWriteGate } from '@/shared/components/MenuWriteGate';
import { PageHeader } from '@/shared/components/PageHeader';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shared/components/ui/alert-dialog';
import { Badge } from '@/shared/components/ui/badge';
import { Button } from '@/shared/components/ui/button';
import { Card, CardContent } from '@/shared/components/ui/card';
import { Checkbox } from '@/shared/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { Field } from '@/shared/components/ui/form-field';
import { Input } from '@/shared/components/ui/input';
import { FilterRow, Stack, ToolbarRow } from '@/shared/components/ui/row';
import { ROUTE_PATHS } from '@/shared/constants/routes';
import { cn } from '@/shared/lib/utils';
import { handleApiError, useToast } from '@/shared/utils/toast';
import { useNavigate, useSearch } from '@tanstack/react-router';
import {
  ChevronDown,
  MinusCircle,
  PlusCircle,
  Search,
  Trash2,
} from 'lucide-react';
import React, { useEffect, useMemo, useRef, useState } from 'react';

const resolveUserId = (user: { id?: number; userId?: number }) =>
  user.userId ?? user.id;

export const BaseFeeRateSettingPage: React.FC = () => {
  const toast = useToast();
  const navigate = useNavigate();
  const { readOnly } = useMenuAccess();
  const search = useSearch({ strict: false }) as {
    settlementYearMonth?: string;
  };

  const initialYm = search.settlementYearMonth?.trim() || getCurrentRefYm();
  const [targetYm, setTargetYm] = useState(initialYm);
  const [rangeRows, setRangeRows] = useState<RangeFeeRowUi[]>(createRangeRows());
  const [fixedRows, setFixedRows] = useState<FixedFeeRowUi[]>(createFixedRows());
  const [isDirty, setIsDirty] = useState(false);
  const [hasSavedPolicy, setHasSavedPolicy] = useState(false);
  const [exitOpen, setExitOpen] = useState(false);
  const [saveOpen, setSaveOpen] = useState(false);
  const [loadPreviousOpen, setLoadPreviousOpen] = useState(false);

  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerSearch, setPickerSearch] = useState('');
  const [pickerTargetRowKey, setPickerTargetRowKey] = useState<string | null>(
    null,
  );
  const [tempSelectedUsers, setTempSelectedUsers] = useState<FixedFeeUserUi[]>(
    [],
  );
  const [userLoading, setUserLoading] = useState(false);
  const [searchedUsers, setSearchedUsers] = useState<FixedFeeUserUi[]>([]);
  /** 수수료율 입력 포커스 중일 때만 원본 표시 (비포커스는 소수점 2자리) */
  const [focusedRateKey, setFocusedRateKey] = useState<string | null>(null);
  const [focusedRateDraft, setFocusedRateDraft] = useState('');
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { data: policy, isLoading, isFetching, refetch } =
    useBasicFeePolicy(targetYm);
  const registerPolicy = useRegisterBasicFeePolicy();
  const copyPolicy = useCopyBasicFeePolicy();

  const busy =
    isLoading ||
    isFetching ||
    registerPolicy.isPending ||
    copyPolicy.isPending;

  useEffect(() => {
    if (search.settlementYearMonth?.trim()) {
      setTargetYm(search.settlementYearMonth.trim());
    }
  }, [search.settlementYearMonth]);

  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, []);

  // 월 변경 시 React Query가 policy를 갱신하면 폼을 서버 상태로 동기화한다.
  useEffect(() => {
    if (isLoading) return;
    if (!policy) {
      setRangeRows(createRangeRows());
      setFixedRows(createFixedRows());
      setHasSavedPolicy(false);
      setIsDirty(false);
      return;
    }
    setHasSavedPolicy(true);
    if (
      policy.settlementYearMonth &&
      policy.settlementYearMonth !== targetYm
    ) {
      setTargetYm(policy.settlementYearMonth);
    }
    setRangeRows(normalizeRangeMins(toRangeRows(policy.rangeFees || [])));
    setFixedRows(toFixedRows(policy.fixedFees || []));
    setIsDirty(false);
    // targetYm은 의도적으로 deps 제외 — 월 피커 변경 직후 이전 policy로 덮어쓰지 않음
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [policy, isLoading]);

  const selectedUserKeysInOtherRows = useMemo(() => {
    const keys = new Set<string>();
    fixedRows.forEach((row) => {
      if (row.key === pickerTargetRowKey) return;
      row.users.forEach((user) => keys.add(user.key));
    });
    return keys;
  }, [fixedRows, pickerTargetRowKey]);

  const updateRangeRow = (key: string, patch: Partial<RangeFeeRowUi>) => {
    setIsDirty(true);
    setRangeRows((prev) =>
      normalizeRangeMins(
        prev.map((row) => (row.key === key ? { ...row, ...patch } : row)),
      ),
    );
  };

  const updateFixedRow = (key: string, patch: Partial<FixedFeeRowUi>) => {
    setIsDirty(true);
    setFixedRows((prev) =>
      prev.map((row) => (row.key === key ? { ...row, ...patch } : row)),
    );
  };

  /** 비포커스: 5 → "5.00" / 포커스: draft 문자열로 편집 */
  const getRateInputValue = (key: string, rate: number) =>
    focusedRateKey === key ? focusedRateDraft : formatRateInput(rate);

  const focusRateInput = (key: string, rate: number) => {
    setFocusedRateKey(key);
    setFocusedRateDraft(formatRateInput(rate));
  };

  const changeRateDraft = (raw: string, apply: (rate: number) => void) => {
    if (raw !== '' && !/^\d*\.?\d*$/.test(raw)) return;
    setFocusedRateDraft(raw);
    if (raw === '' || raw.endsWith('.')) return;
    const next = Number(raw);
    if (Number.isNaN(next)) return;
    apply(next);
  };

  const blurRateInput = (apply: (rate: number) => void) => {
    apply(roundRate(Number(focusedRateDraft) || 0));
    setFocusedRateKey(null);
    setFocusedRateDraft('');
  };

  const addRangeRow = () => {
    setIsDirty(true);
    setRangeRows((prev) => {
      const previousMax = Number(prev[prev.length - 1]?.maxAmount || 0);
      return [
        ...prev,
        {
          key: `r-${Date.now()}`,
          minAmount: previousMax,
          maxAmount: 0,
          rate: 0,
        },
      ];
    });
  };

  const removeRangeRow = (key: string) => {
    setIsDirty(true);
    setRangeRows((prev) =>
      normalizeRangeMins(prev.filter((row) => row.key !== key)),
    );
  };

  const addFixedRow = () => {
    setIsDirty(true);
    setFixedRows((prev) => [
      ...prev,
      { key: `f-${Date.now()}`, rate: 0, users: [] },
    ]);
  };

  const removeFixedRow = (key: string) => {
    setIsDirty(true);
    setFixedRows((prev) => prev.filter((row) => row.key !== key));
  };

  const loadPlatformUsers = async (email: string) => {
    const keyword = email.trim();
    if (!keyword) {
      setSearchedUsers([]);
      return;
    }
    try {
      setUserLoading(true);
      const page = await userService.getUsers(
        { email: keyword, isSalesApproved: true },
        { page: 0, size: 100 },
      );
      const content = Array.isArray(page?.content) ? page.content : [];
      setSearchedUsers(
        content.map((user) => {
          const id = resolveUserId(user);
          const userKey = String(id ?? '');
          return {
            key: userKey,
            companyName: user.companyName || user.ceoName || '-',
            account: user.email || `ID: ${userKey}`,
          };
        }),
      );
    } catch (e) {
      console.error(e);
      setSearchedUsers([]);
    } finally {
      setUserLoading(false);
    }
  };

  const openUserPicker = (rowKey: string) => {
    const target = fixedRows.find((row) => row.key === rowKey);
    setPickerTargetRowKey(rowKey);
    setTempSelectedUsers(target?.users || []);
    setPickerSearch('');
    setSearchedUsers([]);
    setPickerOpen(true);
  };

  const handlePickerSearch = (value: string) => {
    setPickerSearch(value);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      void loadPlatformUsers(value);
    }, 1000);
  };

  const toggleTempUser = (user: FixedFeeUserUi & { disabled?: boolean }) => {
    if (user.disabled) return;
    setTempSelectedUsers((prev) => {
      if (prev.some((item) => item.key === user.key)) {
        return prev.filter((item) => item.key !== user.key);
      }
      return [
        ...prev,
        {
          key: user.key,
          companyName: user.companyName,
          account: user.account,
        },
      ];
    });
  };

  const applyUserPicker = () => {
    if (!pickerTargetRowKey) return;
    const filtered = tempSelectedUsers.filter(
      (user) => !selectedUserKeysInOtherRows.has(user.key),
    );
    updateFixedRow(pickerTargetRowKey, { users: filtered });
    setPickerOpen(false);
    setPickerTargetRowKey(null);
  };

  const goBackToList = () => {
    navigate({ to: ROUTE_PATHS.BASE_FEE_RATES } as never);
  };

  const handleLoadPrevious = async () => {
    const fromYearMonth = getPreviousRefYm(targetYm);
    if (!fromYearMonth) {
      toast.error('전월을 계산할 수 없습니다.');
      return;
    }
    try {
      await copyPolicy.mutateAsync({
        fromYearMonth,
        toYearMonth: targetYm,
      });
      await refetch();
      toast.success('전월 기본 수수료 정보를 불러왔습니다.');
    } catch (e) {
      handleApiError(e, toast);
    }
  };

  const handleClickLoadPrevious = () => {
    if (isDirty || hasSavedPolicy) {
      setLoadPreviousOpen(true);
      return;
    }
    void handleLoadPrevious();
  };

  const handleSave = async () => {
    try {
      const payload = buildBasicFeePolicyPayload(
        targetYm,
        rangeRows,
        fixedRows,
      );
      await registerPolicy.mutateAsync(payload);
      setIsDirty(false);
      setSaveOpen(false);
      await refetch();
      toast.success(
        `${formatRefYmDot(targetYm).replace('. ', '.')} 기본 수수료율이 저장되었습니다.`,
      );
    } catch (e) {
      handleApiError(e, toast);
    }
  };

  if (isLoading && !policy) {
    return <LoadingSpinner tip="기본 수수료 정책을 불러오는 중..." />;
  }

  return (
    <div className="space-y-5">
      <PageHeader title="기본 수수료 설정하기" />

      <Card size="md">
        <CardContent className="space-y-6 p-6">
          <ToolbarRow justify="between" className="gap-3">
            <div className="flex flex-wrap items-end gap-3">
              <Field label="정산 년월" htmlFor="setting-ym">
                <MonthPicker
                  id="setting-ym"
                  value={targetYm}
                  onChange={(next) => {
                    if (!next) return;
                    setTargetYm(next);
                  }}
                  className="w-44"
                />
              </Field>
            </div>
            <MenuWriteGate>
              <Button
                type="button"
                className="ruda-button"
                disabled={busy}
                onClick={handleClickLoadPrevious}
              >
                전 월 정보 가져오기
              </Button>
            </MenuWriteGate>
          </ToolbarRow>

          <div className="border-t border-ruda-border-light" />

          {/* 구간 수수료 */}
          <Stack gap={16}>
            <ToolbarRow justify="between">
              <h3 className="text-base font-bold tracking-tight text-ruda-text-primary">
                구간 수수료
              </h3>
              <MenuWriteGate>
                <Button
                  type="button"
                  variant="outline"
                  className="ruda-button-outline"
                  onClick={addRangeRow}
                >
                  <PlusCircle className="mr-1 h-4 w-4" />
                  추가
                </Button>
              </MenuWriteGate>
            </ToolbarRow>

            <Stack gap={12}>
              {rangeRows.map((row, index) => (
                <div
                  key={row.key}
                  className="flex flex-wrap items-center gap-3 rounded-12 border border-ruda-border-light p-3"
                >
                  <span className="shrink-0 text-sm font-bold text-ruda-text-primary">
                    구간 {index + 1}
                  </span>
                  <div className="relative w-40">
                    <Input
                      type="text"
                      inputMode="numeric"
                      className="pr-8 text-right"
                      value={formatAmountInput(row.minAmount)}
                      disabled={readOnly || index > 0}
                      onChange={(e) =>
                        updateRangeRow(row.key, {
                          minAmount: parseAmountInput(e.target.value),
                        })
                      }
                    />
                    <span className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-xs text-ruda-grey-500">
                      원
                    </span>
                  </div>
                  <span className="text-sm font-medium text-ruda-error">
                    이상
                  </span>
                  <span className="text-ruda-grey-400">---</span>
                  <div className="relative w-40">
                    <Input
                      type="text"
                      inputMode="numeric"
                      className="pr-8 text-right"
                      value={formatAmountInput(row.maxAmount)}
                      disabled={readOnly}
                      onChange={(e) =>
                        updateRangeRow(row.key, {
                          maxAmount: parseAmountInput(e.target.value),
                        })
                      }
                    />
                    <span className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-xs text-ruda-grey-500">
                      원
                    </span>
                  </div>
                  <span className="text-sm font-medium text-ruda-error">
                    미만
                  </span>

                  <div className="mx-1 hidden h-6 w-px bg-ruda-border-light sm:block" />

                  <span className="text-sm text-ruda-grey-700">수수료율</span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="text-ruda-primary hover:bg-ruda-primary-50 hover:text-ruda-primary-dark"
                    disabled={readOnly}
                    onClick={() =>
                      updateRangeRow(row.key, {
                        rate: handleRateStep(row.rate, -0.1),
                      })
                    }
                  >
                    <MinusCircle className="h-4 w-4" />
                  </Button>
                  <div className="relative w-28">
                    <Input
                      type="text"
                      inputMode="decimal"
                      className="pr-8 text-right"
                      value={getRateInputValue(row.key, row.rate)}
                      disabled={readOnly}
                      onFocus={() => focusRateInput(row.key, row.rate)}
                      onBlur={() =>
                        blurRateInput((rate) =>
                          updateRangeRow(row.key, { rate }),
                        )
                      }
                      onChange={(e) =>
                        changeRateDraft(e.target.value.trim(), (rate) =>
                          updateRangeRow(row.key, { rate }),
                        )
                      }
                    />
                    <span className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-xs text-ruda-grey-500">
                      %
                    </span>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="text-ruda-primary hover:bg-ruda-primary-50 hover:text-ruda-primary-dark"
                    disabled={readOnly}
                    onClick={() =>
                      updateRangeRow(row.key, {
                        rate: handleRateStep(row.rate, 0.1),
                      })
                    }
                  >
                    <PlusCircle className="h-4 w-4" />
                  </Button>

                  <div className="mx-1 hidden h-6 w-px bg-ruda-border-light sm:block" />

                  <MenuWriteGate>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="text-ruda-error hover:bg-ruda-error-50 hover:text-ruda-red-700"
                      onClick={() => removeRangeRow(row.key)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </MenuWriteGate>
                </div>
              ))}
            </Stack>
          </Stack>

          <div className="border-t border-ruda-border-light" />

          {/* 고정 수수료 */}
          <Stack gap={16}>
            <ToolbarRow justify="between">
              <h3 className="text-base font-bold tracking-tight text-ruda-text-primary">
                고정 수수료
              </h3>
              <MenuWriteGate>
                <Button
                  type="button"
                  variant="outline"
                  className="ruda-button-outline"
                  onClick={addFixedRow}
                >
                  <PlusCircle className="mr-1 h-4 w-4" />
                  추가
                </Button>
              </MenuWriteGate>
            </ToolbarRow>

            <Stack gap={16}>
              {fixedRows.map((row, index) => (
                <Stack
                  key={row.key}
                  gap={8}
                  className="rounded-12 border border-ruda-border-light p-3"
                >
                  <ToolbarRow justify="between">
                    <FilterRow as="div" gap={8} className="items-center">
                      <span className="text-sm font-bold text-ruda-text-primary">
                        고정 {index + 1}
                      </span>
                      <span className="text-sm text-ruda-grey-700">
                        수수료율
                      </span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="text-ruda-primary hover:bg-ruda-primary-50 hover:text-ruda-primary-dark"
                        disabled={readOnly}
                        onClick={() =>
                          updateFixedRow(row.key, {
                            rate: handleRateStep(row.rate, -0.1),
                          })
                        }
                      >
                        <MinusCircle className="h-4 w-4" />
                      </Button>
                      <div className="relative w-28">
                        <Input
                          type="text"
                          inputMode="decimal"
                          className="pr-8 text-right"
                          value={getRateInputValue(
                            `fixed-${row.key}`,
                            row.rate,
                          )}
                          disabled={readOnly}
                          onFocus={() =>
                            focusRateInput(`fixed-${row.key}`, row.rate)
                          }
                          onBlur={() =>
                            blurRateInput((rate) =>
                              updateFixedRow(row.key, { rate }),
                            )
                          }
                          onChange={(e) =>
                            changeRateDraft(e.target.value.trim(), (rate) =>
                              updateFixedRow(row.key, { rate }),
                            )
                          }
                        />
                        <span className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-xs text-ruda-grey-500">
                          %
                        </span>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="text-ruda-primary hover:bg-ruda-primary-50 hover:text-ruda-primary-dark"
                        disabled={readOnly}
                        onClick={() =>
                          updateFixedRow(row.key, {
                            rate: handleRateStep(row.rate, 0.1),
                          })
                        }
                      >
                        <PlusCircle className="h-4 w-4" />
                      </Button>
                    </FilterRow>
                    <MenuWriteGate>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="text-ruda-error hover:bg-ruda-error-50 hover:text-ruda-red-700"
                        onClick={() => removeFixedRow(row.key)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </MenuWriteGate>
                  </ToolbarRow>

                  <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
                    <MenuWriteGate>
                      <Button
                        type="button"
                        variant="outline"
                        className="ruda-button-outline w-full justify-between sm:w-64"
                        onClick={() => openUserPicker(row.key)}
                      >
                        사용자를 선택하세요
                        <ChevronDown className="h-4 w-4 text-ruda-primary/60" />
                      </Button>
                    </MenuWriteGate>
                    <div className="min-h-[66px] flex-1 rounded-12 border border-ruda-border-light bg-white p-3">
                      {row.users.length === 0 ? (
                        <p className="text-sm text-ruda-grey-500">
                          선택된 사용자가 없습니다.
                        </p>
                      ) : (
                        <div className="flex flex-wrap gap-2">
                          {row.users.map((user) => (
                            <Badge
                              key={user.key}
                              variant="default"
                              className="gap-1 pr-1"
                            >
                              <span>
                                {user.account} | {user.companyName}
                              </span>
                              {!readOnly ? (
                                <button
                                  type="button"
                                  className="rounded px-1 text-ruda-primary/70 hover:text-ruda-primary-dark"
                                  aria-label="선택 해제"
                                  onClick={() =>
                                    updateFixedRow(row.key, {
                                      users: row.users.filter(
                                        (item) => item.key !== user.key,
                                      ),
                                    })
                                  }
                                >
                                  ×
                                </button>
                              ) : null}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </Stack>
              ))}
            </Stack>
          </Stack>

          <ToolbarRow justify="center" className="gap-3 pt-2">
            <Button
              type="button"
              variant="outline"
              size="lg"
              className="ruda-button-outline"
              onClick={() => {
                if (isDirty) {
                  setExitOpen(true);
                  return;
                }
                goBackToList();
              }}
            >
              나가기
            </Button>
            <MenuWriteGate>
              <Button
                type="button"
                className="ruda-button"
                size="lg"
                disabled={!isDirty || busy}
                onClick={() => setSaveOpen(true)}
              >
                설정 완료
              </Button>
            </MenuWriteGate>
          </ToolbarRow>
        </CardContent>
      </Card>

      {/* 사용자 선택 모달 */}
      <Dialog open={pickerOpen} onOpenChange={setPickerOpen}>
        <DialogContent className="max-w-sm" showCloseButton={false}>
          <DialogHeader>
            <DialogTitle>사용자 선택</DialogTitle>
          </DialogHeader>
          <Stack gap={12}>
            <Input
              className="w-full"
              placeholder="사용자 이메일"
              value={pickerSearch}
              onChange={(e) => handlePickerSearch(e.target.value)}
              startIcon={<Search />}
            />
            <div className="max-h-80 overflow-y-auto rounded-12 border border-ruda-border-light">
              {userLoading ? (
                <div className="py-8">
                  <LoadingSpinner tip="검색 중..." />
                </div>
              ) : searchedUsers.length === 0 ? (
                <p className="px-3 py-6 text-center text-sm text-ruda-grey-500">
                  이메일을 검색해 주세요.
                </p>
              ) : (
                <ul className="divide-y divide-ruda-border-light bg-white">
                  {searchedUsers.map((user) => {
                    const disabled = selectedUserKeysInOtherRows.has(user.key);
                    const checked = tempSelectedUsers.some(
                      (item) => item.key === user.key,
                    );
                    return (
                      <li key={user.key}>
                        <label
                          className={cn(
                            'flex cursor-pointer items-start gap-3 px-3 py-3 transition-colors hover:bg-ruda-primary-50',
                            checked && 'bg-ruda-primary-50/60',
                            disabled &&
                              'cursor-not-allowed opacity-50 hover:bg-transparent',
                          )}
                        >
                          <Checkbox
                            checked={checked}
                            disabled={disabled}
                            onCheckedChange={() =>
                              toggleTempUser({ ...user, disabled })
                            }
                          />
                          <span className="space-y-0.5">
                            <span className="block text-sm font-medium text-ruda-text-primary">
                              {user.companyName}
                            </span>
                            <span className="block text-xs text-ruda-grey-500">
                              {user.account}
                            </span>
                          </span>
                        </label>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </Stack>
          <DialogFooter className="gap-2 sm:justify-stretch">
            <Button
              type="button"
              variant="outline"
              className="ruda-button-outline flex-[3]"
              onClick={() => setPickerOpen(false)}
            >
              닫기
            </Button>
            <Button
              type="button"
              className="ruda-button flex-[7]"
              onClick={applyUserPicker}
            >
              선택완료
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={loadPreviousOpen} onOpenChange={setLoadPreviousOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>전월 정보 불러오기</AlertDialogTitle>
            <AlertDialogDescription>
              입력된 정보가 있습니다.
              <br />
              불러오기 시 입력된 정보는 삭제됩니다.
              <br />
              불러오시겠습니까?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>취소</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                setLoadPreviousOpen(false);
                void handleLoadPrevious();
              }}
            >
              확인
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={exitOpen} onOpenChange={setExitOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>나가기</AlertDialogTitle>
            <AlertDialogDescription>
              변경된 정보가 있습니다.
              <br />
              나가시면 변경 내역은 삭제됩니다.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>취소</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setExitOpen(false);
                goBackToList();
              }}
            >
              나가기
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={saveOpen} onOpenChange={setSaveOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>기본 수수료율 저장</AlertDialogTitle>
            <AlertDialogDescription>
              {formatRefYmDot(targetYm).replace('. ', '.')} 부터 적용할 기본
              수수료율을 저장하시겠습니까?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>취소</AlertDialogCancel>
            <AlertDialogAction
              disabled={busy}
              onClick={(e) => {
                e.preventDefault();
                void handleSave();
              }}
            >
              저장
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
